import { useEffect, useState } from 'react';
import { createRoom, getBuildings, getSubjectsList } from 'services/api';
import { useAppForm } from 'shared/UI/UIKit/Form';
import { formatSubjectWithLevel } from 'shared/helpers/utils/subject-display';
import type { AppSelectOption } from 'shared/UI/UIKit/Select';
import type { AddRoomLessonFormValues, AddRoomLessonModalProps } from './types';

export const useAddRoomLessonModal = ({ visible, hide, onSuccess }: AddRoomLessonModalProps) => {
  const [form] = useAppForm<AddRoomLessonFormValues>();
  const [loading, setLoading] = useState(false);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [buildingsLoading, setBuildingsLoading] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState<AppSelectOption[]>([]);
  const [buildingOptions, setBuildingOptions] = useState<AppSelectOption[]>([]);

  useEffect(() => {
    if (!visible) return;

    setSubjectsLoading(true);
    getSubjectsList()
      .then((res) =>
        setSubjectOptions(
          res.content.map(({ id, name, educationLevel }) => ({
            value: id,
            label: formatSubjectWithLevel(name, educationLevel),
          })),
        ),
      )
      .finally(() => setSubjectsLoading(false));

    setBuildingsLoading(true);
    getBuildings()
      .then((res) =>
        setBuildingOptions(
          res.content.map((building) => ({
            value: building.id,
            label: building.name,
          })),
        ),
      )
      .finally(() => setBuildingsLoading(false));
  }, [visible]);

  const hideModal = () => {
    hide();
    form.resetFields();
  };

  const onSubmit = () => {
    form.validateFields().then((values) => {
      setLoading(true);
      createRoom(values)
        .then(() => {
          hideModal();
          onSuccess();
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  return {
    state: {
      form,
      loading,
      subjectsLoading,
      buildingsLoading,
      subjectOptions,
      buildingOptions,
    },
    methods: {
      hideModal,
      onSubmit,
    },
  };
};
